<template>
  <div>
    <b-card
        size="lg"
        :title="$t('pages.product_research.search_product_modal.title')"
        title-class="font-18"
    >
      <b-row>
        <b-col sm="12">
          <div class="mt-3">
            <div>
              <div v-show="fileRecords.length < 1">
                <VueFileAgent
                    v-model="files"
                    :multiple="false"
                    :uploadUrl="false"
                    :helpText="options.helpText"
                    :accept="options.accept"
                    :maxSize="options.maxSize"
                    :linkable="true"
                    :errorText="{type: options.typeError,size: options.sizeError}"
                    @select="filesSelected"
                />
              </div>
              <div v-show="fileRecords.length > 0">
                <div id="cropper-layout">
                  <div id="cropper-parent">
                    <cropper ref="cropper" class="cropper" :src="img"/>
                    <b-btn class="remove-btn" size="sm" variant="danger" @click="removeImage">
                      <i class="mdi mdi-delete font-size-18"></i>
                    </b-btn>
                  </div>
                </div>
              </div>
            </div>
            <div
                v-if="submitted && !$v.img.required"
                class="d-block invalid-feedback"
            >
              {{ $t('pages.product_research.search_product_modal.validation.img_required') }}
            </div>
          </div>
        </b-col>
        <b-col sm="12" class="mt-3">
          <div>
            <b-form-group
                class="mb-2"
                :label="$t('pages.product_research.search_product_modal.marketplace')"
                label-for="marketplace"
                label-cols="12"
                :description="$t('pages.product_research.search_product_modal.marketplace_description')"
            >
              <market-place-component
                  v-model="marketplace"
                  :invalid="submitted && $v.marketplace.$error"
                  id="marketplace"
                  :multiple="false"
                  :all="true"
              />
              <div
                  v-if="submitted && !$v.marketplace.required"
                  class="d-block invalid-feedback"
              >
                {{ $t('pages.settings.connect_marketplace_modal.validations.marketplace_required') }}
              </div>
            </b-form-group>
            <b-form-group
                class="mb-2"
                :label="$t('pages.product_research.search_product_modal.search_term')"
                label-for="search"
                label-cols="12"
            >
              <b-form-input
                  :placeholder="$t('pages.product_research.search_product_modal.search_term_placeholder')"
                  v-model="searchTerm"
                  id="search"
                  type="text"
                  :class="{'is-invalid': submitted && $v.searchTerm.$error}"
              ></b-form-input>
              <div
                  v-if="submitted && !$v.searchTerm.required"
                  class="d-block invalid-feedback"
              >
                {{ $t('pages.product_research.search_product_modal.validation.search_required') }}
              </div>
              <div
                  v-if="submitted && !$v.searchTerm.minLength"
                  class="d-block invalid-feedback"
              >
                {{
                  $t('pages.product_research.search_product_modal.validation.search_min', {min: $v.searchTerm.$params.minLength.min})
                }}
              </div>
              <div
                  v-if="submitted && !$v.searchTerm.maxLength"
                  class="d-block invalid-feedback"
              >
                {{
                  $t('pages.product_research.search_product_modal.validation.search_max', {max: $v.searchTerm.$params.maxLength.max})
                }}
              </div>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
      <div class="text-end mt-3">
        <b-button variant="success" @click="save" :disabled="loading || disabled">
          <b-spinner v-if="loading" small></b-spinner>
          {{ $t('pages.product_research.search_product_modal.search') }}
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import {Cropper} from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import MarketPlaceComponent from "../../../../components/widgets/MarketPlaceComponent";
import ProductResearchService from "../../../../Services/Api/ProductResearchService";
import {minLength, maxLength, required} from "vuelidate/lib/validators";

export default {
  name: "SearchProductCard",
  props: {
    disabled: {
      default: false
    }
  },
  data() {
    return {
      img: null,
      fileRecords: [],
      files: [],
      marketplace: null,
      searchTerm: '',
      loading: false,
      submitted: false,
      options: {
        accept: 'image/jpg,image/jpeg,image/png',
        maxSize: '2MB',
        typeError: this.$t('pages.product_research.search_product_modal.file_options.typeError'),
        sizeError: this.$t('pages.product_research.search_product_modal.file_options.sizeError'),
        helpText: this.$t('pages.product_research.search_product_modal.file_options.helpText'),
        acceptText: '(png, jpeg, jpg, only - 1mb max)'
      },
    };
  },
  validations: {
    searchTerm: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(255)
    },
    marketplace: {
      required
    },
    img: {
      required
    }
  },
  methods: {
    removeImage() {
      this.img = null
      this.files = []
      this.fileRecords = []
    },
    async filesSelected(fileRecordsNewlySelected) {
      this.fileRecords = []
      const validFileRecords = fileRecordsNewlySelected.filter((fileRecord) => !fileRecord.error);
      this.fileRecords = this.fileRecords.concat(validFileRecords);

      if (this.fileRecords.length > 0) {
        this.img = await this.getBase64String(this.fileRecords[0]?.file)
      }
    },
    getBase64String(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // replace the data: part depending on your need
          const base64String = reader.result.toString()/* .replace(/^data:(.*,)?/, '') */
          resolve(base64String)
        }
        reader.onerror = (error) => {
          reject(error)
        }
        reader.readAsDataURL(file)
      })
    },
    getCroppedImage() {
      const {canvas} = this.$refs.cropper.getResult();
      return canvas.toDataURL();
    },
    setDefault() {
      this.img = null
      this.fileRecords = []
      this.files = []
      this.marketplace = null
      this.searchTerm = ''
    },
    save() {
      this.submitted = true
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      } else {
        this.loading = true

        const payload = {
          'marketplace_id': this.marketplace.marketplace_id,
          'search': this.searchTerm,
          'image': this.getCroppedImage()
        }

        ProductResearchService.store({payload}).then(() => {
          this.$emit('save')
          this.setDefault()
          this.$store.dispatch('quota/getSubscriptionQuota')
        }).finally(() => {
          this.loading = false
          this.submitted = false
        })
      }
    }
  },
  components: {
    MarketPlaceComponent,
    Cropper,
  },
};
</script>

<style scoped>
#cropper-parent {
  position: relative;
  background-color: #b0b0b0;
  flex: 1;
}

.remove-btn {
  position: absolute;
  right: 6px;
  top: 6px;
}

/deep/ .vue-advanced-cropper__foreground {
  background-image: linear-gradient(45deg, #b0b0b0 25%, transparent 25%), linear-gradient(-45deg, #b0b0b0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #b0b0b0 75%), linear-gradient(-45deg, transparent 75%, #b0b0b0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.cropper {
  min-height: 280px;
  width: 100%;
  height: 100%;
}

/deep/ .vue-file-agent .file-preview-new:before {
  background: none;
}

/deep/ .vue-file-agent .file-preview-wrapper:before {
  background: none;
}

/deep/ .vue-file-agent {
  background: rgba(0, 0, 0, .05) !important;
  border-radius: 6px !important;
}

/deep/ .multiselect__content-wrapper {
  max-height: 160px !important;
}
</style>